import React, { useEffect, useRef, useState } from 'react';
import {
  useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  getCauzioniCatalogue, getCauzioniRequest, getCauzioniRiskDocuments,
  getCauzioniRiskDocumentsBackoffice, submitCauzioniRequest, submitCauzioniRequestBackoffice,
  uploadDocumentBackoffice,
} from '../../services/axios-client/axeCauzioni';
import { uploadDocument } from '../../services/axios-client/axeCommons';
import quotationsAxe from '../../services/axios-client/quotationsAxeClient';
import { quotationsBackofficeAxe } from '../../services/axios-client/quotationsBackofficeAxeClient';
import ProgressBar from '../../common/componentsV2/ProgressBar';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import QuoteGenerateLoading from '../../common/componentsV2/elements/QuoteGenerateLoading';
import { generalAlertError } from '../../common/utils/alerts';
import {
  beneficiarioSchema, cauzioniDefaultValues, contattiSchema, contraenteSchema, garanziaSchema,
} from './CauzioniRequest.validations';
import StepContraente from './steps/StepContraente';
import StepContatti from './steps/StepContatti';
import StepBeneficiario from './steps/StepBeneficiario';
import StepGaranzia from './steps/StepGaranzia';
import StepDocumenti from './steps/StepDocumenti';
import StepPrivacy from './steps/StepPrivacy';
import { buildPrefillValues, readDocumentPrefill } from './documentPrefill';
import { V2FontImport } from '../../common/formInputsV2/V2Fonts';
import { PageHeading, PageHeadingTitle, PageHeadingSubtitle } from '../../common/formInputsV2/PageHeading.styles';
import { FormLayout } from '../../common/formInputsV2/FormLayout.styles';
import { FooterNav } from '../../common/formInputsV2/FooterNav.styles';
import { PrimaryButton } from '../../common/formInputsV2/Button.styles';

// Mirrors NonSignableDocumentComponent's FileReader → base64 → upload sequence
// (src/screens/purchase/common/NonSignableDocumentComponent.jsx:13-41), minus quotation_id:
// Cauzioni documents are request-scoped and Task 5 made quotation_id optional for product 1007.
const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.replace(`data:${file.type};base64,`, ''));
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/* Uploads sequentially rather than in parallel: 13 base64 payloads fired at once is a lot of
   memory and a lot of concurrent PHP workers for no gain the broker can perceive. Never
   rejects — it resolves with the document_type_ids that failed, so a partial failure keeps the
   request and reports precisely what is missing instead of collapsing into one opaque error. */
const uploadStagedDocuments = (requestId, stagedFiles, backoffice = false) => Object.entries(stagedFiles).reduce(
  (chain, [documentTypeId, file]) => chain.then(async (failed) => {
    try {
      const unsignedDocument = await readAsBase64(file);
      const upload = backoffice ? uploadDocumentBackoffice : uploadDocument;
      const response = await upload({
        document_type_id: Number(documentTypeId),
        request_id: requestId,
        original_filename: file.name,
        unsigned_document: unsignedDocument,
      });
      if (response.status !== 200) failed.push(documentTypeId);
    } catch (e) {
      failed.push(documentTypeId);
    }
    return failed;
  }),
  Promise.resolve([]),
);

const StepIdType = {
  CONTRAENTE: 'contraente',
  CONTATTI: 'contatti',
  BENEFICIARIO: 'beneficiario',
  GARANZIA: 'garanzia',
  DOCUMENTI: 'documenti',
  PRIVACY: 'privacy',
};

const steps = [
  { id: StepIdType.CONTRAENTE, title: 'Contraente' },
  { id: StepIdType.CONTATTI, title: 'Contatti' },
  { id: StepIdType.BENEFICIARIO, title: 'Beneficiario' },
  { id: StepIdType.GARANZIA, title: 'Dati Garanzia' },
  { id: StepIdType.DOCUMENTI, title: 'Documenti' },
  { id: StepIdType.PRIVACY, title: 'Privacy' },
];

const stepTitles = steps.map((s) => s.title);

/* Form state is flat (every field a top-level key); the submission endpoint's Joi schema
   is nested. beneficiary_* keys drop their prefix, contact_* keys drop theirs — everything
   else maps straight across. guaranteed_amount arrives as FormInputPrice's raw string and
   must be converted, since the Joi schema declares it Joi.number(). */
const buildCauzioniPayload = (formData, selection, privacyConsents = {}) => ({
  sub_product_id: selection.sub.id,
  risk_id: selection.risk.id,
  customer: {
    gender: formData.gender,
    business_name: formData.business_name,
    vat_number: formData.vat_number,
    /* Joi declares this Joi.number(), and the form holds '' for a Persona Fisica — which is
       neither a number nor null, so submitting as Maschio/Femmina was rejected with
       "customer.company_type must be a number". Only a Persona Giuridica has one at all. */
    company_type: formData.gender === 'G' ? formData.company_type || null : null,
    fiscal_code: formData.fiscal_code,
    name: formData.name,
    surname: formData.surname,
    date_of_birth: formData.date_of_birth,
    address: formData.address,
    house_number: formData.house_number,
    postal_code: formData.postal_code,
    residence_commune_code: formData.residence_commune_code,
    residence_province_code: formData.residence_province_code,
    email: formData.email,
    phone: formData.phone,
    pec: formData.pec,
    active_customer_id: formData.active_customer_id,
  },
  beneficiary: {
    business_name: formData.beneficiary_business_name,
    /* Folded into the address: there is no beneficiary_house_number column, but the shared
       address widget always collects an "N.", and dropping it would store a street with no
       number for a beneficiary the policy has to name precisely. */
    address: [formData.beneficiary_address, formData.beneficiary_house_number]
      .filter(Boolean).join(' ').trim() || null,
    locality: formData.beneficiary_locality,
    province: formData.beneficiary_province,
    postal_code: formData.beneficiary_postal_code,
    vat_or_fiscal_code: formData.beneficiary_vat_or_fiscal_code,
    pec: formData.beneficiary_pec,
  },
  guarantee: {
    effective_date: formData.effective_date,
    expiry_date: formData.expiry_date,
    guaranteed_amount: Number(formData.guaranteed_amount) || 0,
    description: formData.description,
    notes: formData.notes,
  },
  contact: {
    name: formData.contact_name,
    phone: formData.contact_phone,
    email: formData.contact_email,
  },
  extra_data: formData.extra_data || {},
  privacy: {
    consent_marketing: privacyConsents.marketing ?? false,
    consent_third_party: privacyConsents.thirdParty ?? false,
  },
});

/* Wizard shell for the request flow that follows the phase-1 catalogue selection. Owns a
   single useForm instance so every step reads and writes the same values, and resolves the
   sub-product/rischio from the catalogue like the phase-1 placeholder did. */
/* Maps a saved request back onto the flat form shape. Only what the form owns: sub-product and
   rischio come from the URL, and documents are deliberately not carried over — a re-quote is a
   new request, so its files are staged and uploaded fresh against the new id. */
const prefillFromRequest = (detail) => {
  const c = detail.contractor || {};
  const b = detail.beneficiary || {};
  const g = detail.guarantee || {};
  const ct = detail.contact || {};

  return {
    gender: c.gender || 'G',
    business_name: c.business_name || '',
    vat_number: c.vat_number || '',
    company_type: c.company_type ?? '',
    fiscal_code: c.fiscal_code || '',
    name: c.name || '',
    surname: c.surname || '',
    address: c.address_street || '',
    house_number: c.house_number || '',
    postal_code: c.postal_code || '',
    residence_commune_code: c.residence_commune_code || '',
    residence_province_code: c.province || '',
    email: c.email || '',
    pec: c.pec || '',
    phone: c.phone || '',
    /* Open the optional section when the request being re-quoted actually carries a referente —
       otherwise the backoffice would see the flag off and the saved contact silently dropped on
       submit, because toggling it off is what clears those three fields. */
    has_contact_person: Boolean(ct.name || ct.phone || ct.email),
    contact_name: ct.name || '',
    contact_phone: ct.phone || '',
    contact_email: ct.email || '',
    beneficiary_business_name: b.business_name || '',
    beneficiary_address: b.address || '',
    beneficiary_locality: b.locality || '',
    beneficiary_province: b.province || '',
    beneficiary_postal_code: b.postal_code || '',
    beneficiary_vat_or_fiscal_code: b.vat_or_fiscal_code || '',
    beneficiary_pec: b.pec || '',
    effective_date: (g.effective_date || '').slice(0, 10),
    expiry_date: (g.expiry_date || '').slice(0, 10),
    guaranteed_amount: g.guaranteed_amount ? String(Math.trunc(Number(g.guaranteed_amount))) : '',
    description: g.description || '',
    notes: g.notes || '',
  };
};

function CauzioniRequest({
  backoffice = false,
  backofficeRequestToken = null,
  initialSelection = null,
  initialValues = null,
  onSubmitted = null,
}) {
  const { subProductSlug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const riskId = Number(params.get('risk'));
  // "Modifica Dati" reopens this wizard with the request being copied. Like every other product,
  // editing does not mutate the original — submitting creates a NEW request, so the old one and
  // its documents stay exactly as they were.
  const prefillToken = location.state?.requestToken ?? null;

  const [selection, setSelection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [documentiComplete, setDocumentiComplete] = useState(false);
  const [consents, setConsents] = useState({ marketing: null, thirdParty: null, attempted: false });
  // Files chosen on the documenti step, keyed by document_type_id. They are held here — not
  // uploaded — until submit, because an upload needs a request to attach to and the request
  // is deliberately created last. Kept as File objects so a staged document costs a reference
  // rather than a base64 string in memory.
  const [stagedFiles, setStagedFiles] = useState({});
  // Survives a partial upload: if the request is created but some documents fail to upload,
  // a retry must push the remaining files at the SAME request instead of creating a second one.
  const createdRequestRef = useRef(null);
  const [municipalities, setMunicipalities] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  // Cauzioni has no redux forms slice (and shouldn't gain one just for this). Comune and
  // tipologia-azienda are the same reference lists every other product reads from
  // quotationsAxe — a silent empty-array fallback here would let a broker type a display
  // name into what is actually a cadastral-code/plate-symbol column, so a failed fetch is
  // surfaced instead of swallowed.
  const [formDataError, setFormDataError] = useState(false);
  // Which fields were filled in from an uploaded document, so those inputs — and only those —
  // can tell the broker where the value came from and ask them to check it.
  const [prefilledFields, setPrefilledFields] = useState(() => new Set());
  /* Runs once the reference lists are in, because resolving the comune the document names into
     the cadastral code the form stores needs them. A ref rather than a flag in state: this must
     never re-run and overwrite something that has since been corrected. */
  const prefillApplied = useRef(false);

  useEffect(() => {
    // In backoffice mode the sub-product and rischio come from the request being copied — its
    // URL is /backoffice/quote/:requestToken/surety-bonds and carries no slug to resolve.
    if (initialSelection) {
      setSelection(initialSelection);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    getCauzioniCatalogue()
      .then((res) => {
        if (cancelled) return;
        const sub = res.sub_products.find((s) => s.slug === subProductSlug);
        const risk = sub?.risks.find((r) => r.id === riskId);
        setSelection(sub && risk ? { sub, risk } : null);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [subProductSlug, riskId]);

  useEffect(() => {
    let cancelled = false;

    // Backoffice sessions carry no broker token, so these reference lists must come through the
    // backoffice client — the broker one answers 401 and the comune/tipologia selects come up
    // empty behind "Impossibile caricare l'elenco comuni".
    const formDataClient = backoffice ? quotationsBackofficeAxe : quotationsAxe;

    Promise.all([
      formDataClient.get('/form-data/communes'),
      formDataClient.get('/form-data/service-models'),
    ])
      .then(([communesRes, serviceModelsRes]) => {
        if (cancelled) return;
        const communes = communesRes.data?.data;
        const companyTypesList = serviceModelsRes.data?.data?.company_types;
        if (!Array.isArray(communes) || !Array.isArray(companyTypesList)) {
          throw new Error('Unexpected form-data response');
        }
        setMunicipalities(communes);
        setCompanyTypes(companyTypesList);
      })
      .catch(() => { if (!cancelled) setFormDataError(true); });

    return () => { cancelled = true; };
  }, [backoffice]);

  const {
    register,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    watch,
    control,
    reset,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(
      contraenteSchema.concat(contattiSchema).concat(beneficiarioSchema).concat(garanziaSchema),
    ),
    shouldFocusError: true,
    defaultValues: cauzioniDefaultValues,
  });

  const formData = watch();

  useEffect(() => {
    /* Never over a "Modifica Dati" copy: that path reset()s the form from a real saved request,
       and a document read earlier in the same tab would silently overwrite the customer's actual
       stored data with whatever a PDF happened to say. */
    if (prefillApplied.current || prefillToken || !municipalities.length) return;

    const extracted = readDocumentPrefill();
    if (!extracted) return;

    prefillApplied.current = true;

    const { values, fields } = buildPrefillValues(extracted, municipalities, companyTypes);

    Object.entries(values).forEach(([field, value]) => {
      setValue(field, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    });

    setPrefilledFields(new Set(fields));
  }, [municipalities, companyTypes, setValue, prefillToken]);

  /* Prefill for a "Modifica Dati" copy. Runs after useForm exists, so it uses reset() rather
     than defaultValues — the token only arrives via router state, which is not available at the
     time defaultValues is evaluated. */
  useEffect(() => {
    if (initialValues) {
      reset({ ...cauzioniDefaultValues, ...initialValues }, { keepDefaultValues: true });
      return undefined;
    }

    if (!prefillToken) return undefined;
    let cancelled = false;

    getCauzioniRequest(prefillToken)
      .then((detail) => {
        if (cancelled) return;
        reset({ ...cauzioniDefaultValues, ...prefillFromRequest(detail) }, {
          keepDefaultValues: true,
        });
        if (detail.privacy) {
          setConsents({
            marketing: detail.privacy.consent_marketing ?? null,
            thirdParty: detail.privacy.consent_third_party ?? null,
            attempted: false,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          generalAlertError('Impossibile caricare i dati della richiesta da modificare.');
        }
      });

    return () => { cancelled = true; };
  }, [prefillToken, initialValues]);

  const isValid = (name) => {
    if (errors[name]?.message !== undefined) return false;
    if (touchedFields[name] === undefined) return false;
    return true;
  };

  const step = steps[currentStepIndex];

  /* One submit path, two destinations: the broker's own create endpoint, or the backoffice
     re-quote endpoint that attributes the new request to the original broker. */
  const submitRequest = (payload) => (backoffice
    ? submitCauzioniRequestBackoffice(backofficeRequestToken, payload)
    : submitCauzioniRequest(payload));

  const handleStageDocument = (documentTypeId, file) => {
    setStagedFiles((prev) => {
      const next = { ...prev };
      if (file) {
        next[documentTypeId] = file;
      } else {
        delete next[documentTypeId];
      }
      return next;
    });
  };

  const renderStepComponent = () => {
    switch (step.id) {
      case StepIdType.CONTRAENTE:
        return (
          <StepContraente
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            isValid={isValid}
            municipalities={municipalities}
            companyTypes={companyTypes}
            formDataError={formDataError}
            prefilled={prefilledFields}
          />
        );
      case StepIdType.CONTATTI:
        return (
          <StepContatti
            register={register}
            errors={errors}
            isValid={isValid}
            watch={watch}
            setValue={setValue}
          />
        );
      case StepIdType.BENEFICIARIO:
        return (
          <StepBeneficiario
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            isValid={isValid}
            municipalities={municipalities}
            prefilled={prefilledFields}
          />
        );
      case StepIdType.GARANZIA:
        return (
          <StepGaranzia
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            isValid={isValid}
            control={control}
            subProduct={selection.sub}
            prefilled={prefilledFields}
          />
        );
      case StepIdType.DOCUMENTI:
        return (
          <StepDocumenti
            riskId={selection.risk.id}
            getRiskDocuments={backoffice
              ? getCauzioniRiskDocumentsBackoffice
              : getCauzioniRiskDocuments}
            stagedFiles={stagedFiles}
            onStage={handleStageDocument}
            onCompletionChange={setDocumentiComplete}
          />
        );
      case StepIdType.PRIVACY:
        return (
          <StepPrivacy
            consents={consents}
            onConsentChange={(key, value) => setConsents((prev) => ({ ...prev, [key]: value }))}
          />
        );
      default:
        return null;
    }
  };

  const handleNextStep = () => {
    let stepValidation = null;
    let stepFields = [];

    switch (step.id) {
      case StepIdType.CONTRAENTE:
        stepValidation = contraenteSchema;
        stepFields = Object.keys(contraenteSchema.fields);
        break;
      case StepIdType.CONTATTI:
        stepValidation = contattiSchema;
        stepFields = Object.keys(contattiSchema.fields);
        break;
      case StepIdType.BENEFICIARIO:
        stepValidation = beneficiarioSchema;
        stepFields = Object.keys(beneficiarioSchema.fields);
        break;
      default:
        stepValidation = null;
        stepFields = [];
        break;
    }

    if (stepValidation) {
      const isValidStep = stepValidation.isValidSync(formData);
      if (!isValidStep) {
        trigger(stepFields, { shouldFocus: true }).then(() => {
          generalAlertError('Per favore, completa i campi richiesti correttamente.');
        });
        return;
      }
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBackStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  /* Terminal action of the whole wizard. Order matters: the InsuranceRequest is created
     first because an upload has to attach to something, but nothing is created until the
     broker has every document in hand — so abandoning the flow leaves no half-documented
     request behind. If the POST succeeds and an upload then fails, the request id is kept in
     createdRequestRef and a retry re-sends only the documents still missing, never a second
     request. */
  const handleSubmitRequest = () => {
    const isValidStep = garanziaSchema.isValidSync(formData);
    if (!isValidStep) {
      trigger(Object.keys(garanziaSchema.fields), { shouldFocus: true }).then(() => {
        generalAlertError('Per favore, completa i campi richiesti correttamente.');
      });
      return;
    }

    if (consents.marketing !== true || consents.thirdParty !== true) {
      setConsents((prev) => ({ ...prev, attempted: true }));
      generalAlertError('Per proseguire è necessario prestare entrambi i consensi privacy.');
      return;
    }

    setSubmitting(true);

    const createRequest = createdRequestRef.current
      ? Promise.resolve(createdRequestRef.current)
      : submitRequest(buildCauzioniPayload(formData, selection, consents)).then((res) => {
        if (res.status === 200 && res.data?.request_token) {
          createdRequestRef.current = { id: res.data.id, token: res.data.request_token };
          return createdRequestRef.current;
        }

        const message = Array.isArray(res.data?.error)
          ? res.data.error.map((detail) => detail.message).join('\n')
          : "Qualcosa è andato storto. Chiamare l'amministratore del sistema.";
        throw new Error(message);
      });

    createRequest
      .then((request) => uploadStagedDocuments(request.id, stagedFiles, backoffice)
        .then((failed) => {
          /* Keep only what failed. Without this a retry re-sends documents that are already
             stored, and the upload endpoint rejects a second copy of the same type with
             "Document can not be uploaded" — so the retry looks like a new failure. */
          setStagedFiles((prev) => Object.fromEntries(
            Object.entries(prev).filter(([documentTypeId]) => failed.includes(documentTypeId)),
          ));

          if (failed.length) {
            // The request exists; only the listed documents are missing. Leave the broker on
            // this step with their files still staged so the retry costs one click.
            generalAlertError(
              `Richiesta creata, ma ${failed.length} documenti non sono stati caricati. Riprova.`,
            );
            return;
          }
          if (onSubmitted) {
            onSubmitted(request.token);
            return;
          }
          navigate(`/quotes/surety-bonds/${request.token}`);
        }))
      .catch((err) => {
        generalAlertError(err?.message || 'Impossibile inviare la richiesta. Riprova più tardi.');
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const schemaByStep = {
      [StepIdType.CONTRAENTE]: contraenteSchema,
      [StepIdType.CONTATTI]: contattiSchema,
      [StepIdType.BENEFICIARIO]: beneficiarioSchema,
      [StepIdType.GARANZIA]: garanziaSchema,
    };
    const schema = schemaByStep[steps[currentStepIndex]?.id];
    if (schema) trigger(Object.keys(schema.fields));
  }, [currentStepIndex]);

  if (loading) {
    return <PageLoading />;
  }

  /* Submitting is a long step — it creates the request and then uploads every staged document
     one at a time — so it gets the same countdown screen the quote-generating products use
     rather than a disabled button and no feedback. */
  if (submitting) {
    return (
      <QuoteGenerateLoading
        countNr={30}
        message="Invio della richiesta in corso..."
      />
    );
  }

  if (error) {
    return (
      <div className="cauzioni-page">
        <p className="cauzioni-count cauzioni-error">
          Impossibile caricare la garanzia. Riprova più tardi.
        </p>
      </div>
    );
  }

  if (!selection) {
    return (
      <div className="cauzioni-page">
        <p className="cauzioni-count">Garanzia non trovata.</p>
      </div>
    );
  }

  return (
    <div className="cauzioni-page cauzioni-page-request" data-testid="cauzioni-request-form">
      <V2FontImport />

      <PageHeading>
        <PageHeadingTitle>RICHIESTA POLIZZA</PageHeadingTitle>
        <PageHeadingSubtitle>{`${selection.sub.name} — ${selection.risk.name}`}</PageHeadingSubtitle>
      </PageHeading>

      <div className="d-flex align-content-center justify-content-center">
        <ProgressBar activeStep={currentStepIndex + 1} steps={stepTitles} />
      </div>

      <FormLayout className="mt-4">
        {renderStepComponent()}
      </FormLayout>

      {/* The platform's questionnaire buttons, centred with a gap — same footer as InfoDriveFlow
          and the other funnels, and the same as the public site's cauzioni wizard. Pushing them
          to opposite edges stranded "Continua" at the far right of a page whose form sits in a
          600px column. */}
      <FooterNav className="footer-buttons">
        <div className="d-flex justify-content-center gap-3">
          {currentStepIndex > 0 && (
            <PrimaryButton
              type="button"
              data-testid="cauzioni-back-step"
              onClick={handleBackStep}
            >
              Indietro
            </PrimaryButton>
          )}
          {step.id === StepIdType.PRIVACY ? (
            <PrimaryButton
              type="button"
              data-testid="cauzioni-submit"
              onClick={handleSubmitRequest}
              disabled={submitting || !documentiComplete}
            >
              {submitting ? 'Invio in corso...' : 'Invia richiesta'}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              type="button"
              data-testid="cauzioni-next"
              onClick={handleNextStep}
            >
              Continua
            </PrimaryButton>
          )}
        </div>
      </FooterNav>
    </div>
  );
}

export default CauzioniRequest;
